import { Columns3, RectangleVertical, RectangleHorizontal, Palette, Droplets, Settings2, ChevronDown, FileBox, IndentIncrease, Search, Printer, ScanText, MessageSquarePlus, MessagesSquare, ListChecks, GitCompareArrows, ListTree, Check, Undo2, Sparkles } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../../ui/dropdown-menu";
import { RB, Group, NSelect } from "./parts";
import { MARGIN_PRESETS, PAGE_SIZES, THEMES } from "../../../lib/pages";

const DropMenu = ({ icon: Icon, label, testid, children }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <button type="button" className="rb rb-tall" data-testid={testid} onMouseDown={(e) => e.preventDefault()}>
        <Icon className="h-5 w-5" strokeWidth={1.75} /><span className="flex items-center">{label}<ChevronDown className="h-3 w-3 opacity-60" /></span>
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>{children}</DropdownMenuContent>
  </DropdownMenu>
);

const check = (on) => (on ? " ✓" : "");

export const LayoutTab = ({ settings, setSettings, openDialog }) => {
  const set = (patch) => setSettings({ ...settings, ...patch });
  return (
    <>
      <Group label="Page setup">
        <DropMenu icon={Columns3} label="Margins" testid="rb-margins">
          {Object.entries(MARGIN_PRESETS).map(([k, p]) => (
            <DropdownMenuItem key={k} onSelect={() => set({ margins: { ...p.m } })} data-testid={`margins-${k}`}>{p.label}{check(JSON.stringify(p.m) === JSON.stringify(settings.margins))}</DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => openDialog("pagesetup", "margins")} data-testid="margins-custom">Custom margins…</DropdownMenuItem>
        </DropMenu>
        <DropMenu icon={settings.orientation === "landscape" ? RectangleHorizontal : RectangleVertical} label="Orientation" testid="rb-orientation">
          <DropdownMenuItem onSelect={() => set({ orientation: "portrait" })} data-testid="orientation-portrait"><RectangleVertical className="h-4 w-4 mr-2" />Portrait{check(settings.orientation === "portrait")}</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => set({ orientation: "landscape" })} data-testid="orientation-landscape"><RectangleHorizontal className="h-4 w-4 mr-2" />Landscape{check(settings.orientation === "landscape")}</DropdownMenuItem>
        </DropMenu>
        <DropMenu icon={FileBox} label="Size" testid="rb-page-size">
          {Object.entries(PAGE_SIZES).map(([k, p]) => (
            <DropdownMenuItem key={k} onSelect={() => set({ size: k })} data-testid={`page-size-${k}`}>{p.label}{check(settings.size === k)}</DropdownMenuItem>
          ))}
        </DropMenu>
        <RB tall icon={Settings2} label="Page Setup" onClick={() => openDialog("pagesetup", "paper")} testid="rb-page-setup" />
      </Group>
      <Group label="Design">
        <DropMenu icon={Palette} label="Themes" testid="rb-themes">
          {Object.entries(THEMES).map(([k, t]) => (
            <DropdownMenuItem key={k} onSelect={() => set({ theme: k })} data-testid={`theme-${k}`}>
              <span className="h-4 w-4 rounded-sm border mr-2" style={{ background: t.bg, boxShadow: `inset 0 -4px 0 ${t.heading}` }} />
              <span style={{ fontFamily: t.font }}>{t.label}</span>{check(settings.theme === k)}
            </DropdownMenuItem>
          ))}
        </DropMenu>
        <DropMenu icon={Droplets} label="Watermark" testid="rb-watermark">
          <DropdownMenuLabel className="text-xs">Text watermark</DropdownMenuLabel>
          {["DRAFT", "CONFIDENTIAL", "SAMPLE", "DO NOT COPY", "URGENT"].map((w) => (
            <DropdownMenuItem key={w} onSelect={() => set({ watermark: { ...settings.watermark, text: w } })} data-testid={`watermark-${w.replace(/\s/g, "-").toLowerCase()}`}>{w}{check(settings.watermark.text === w)}</DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => openDialog("pagesetup", "watermark")} data-testid="watermark-custom">Custom watermark…</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => set({ watermark: { ...settings.watermark, text: "" } })} data-testid="watermark-remove">Remove watermark</DropdownMenuItem>
        </DropMenu>
      </Group>
      <Group label="Tab stops">
        <div className="flex flex-col items-start gap-1 px-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><IndentIncrease className="h-3.5 w-3.5" />Default tab stop</div>
          <NSelect width={120} label="Tab stop interval" testid="rb-tab-stops" value={String(settings.tabSize)}
            options={[[24, "0.25 in"], [36, "0.375 in"], [48, "0.5 in"], [72, "0.75 in"], [96, "1 in"], [144, "1.5 in"]].map(([v, l]) => ({ value: String(v), label: l }))}
            onChange={(v) => set({ tabSize: Number(v) })} />
          <div className="text-[10px] text-muted-foreground">Press Tab in text to jump</div>
        </div>
      </Group>
    </>
  );
};

export const ReviewTab = ({ openPanel, openDialog, words, chars, addComment, tracking, toggleTrack, resolveAll }) => (
  <>
    <Group label="Editing">
      <RB tall icon={Search} label="Find & Replace" onClick={() => openPanel("find")} testid="rb-find-replace" />
    </Group>
    <Group label="Comments">
      <RB tall icon={MessageSquarePlus} label="New comment" onClick={addComment} testid="rb-new-comment" />
      <RB tall icon={MessagesSquare} label="Show comments" onClick={() => openPanel("comments")} testid="rb-show-comments" />
    </Group>
    <Group label="AI">
      <RB tall icon={Sparkles} label="AI assistant" onClick={() => openPanel("ai")} testid="rb-ai-assistant" />
    </Group>
    <Group label="Tracking">
      <RB tall icon={GitCompareArrows} label={tracking ? "Tracking on" : "Track changes"} active={tracking} onClick={toggleTrack} testid="rb-track-changes" />
      <RB tall icon={ListTree} label="Changes" onClick={() => openPanel("changes")} testid="rb-changes-panel" />
      <RB tall icon={Check} label="Accept all" onClick={() => resolveAll(true)} testid="rb-accept-all" />
      <RB tall icon={Undo2} label="Reject all" onClick={() => resolveAll(false)} testid="rb-reject-all" />
    </Group>
    <Group label="Signing">
      <RB tall icon={ListChecks} label="Checklist" onClick={() => openPanel("sign")} testid="rb-signing-checklist" />
    </Group>
    <Group label="Proofing">
      <div className="flex items-center gap-2 px-2 text-sm" data-testid="word-count">
        <ScanText className="h-5 w-5 text-muted-foreground" strokeWidth={1.75} />
        <div><div className="font-medium tabular-nums">{words} words</div><div className="text-xs text-muted-foreground tabular-nums">{chars} characters</div></div>
      </div>
    </Group>
    <Group label="Output">
      <RB tall icon={Printer} label="Print Preview" onClick={() => openDialog("preview")} testid="rb-print-preview" />
    </Group>
  </>
);
